import { useState, useEffect } from 'react';
import { releaseService } from '../../backend/services/releases';
import { supabase } from '../../backend/services/supabase';
import { toast } from 'sonner';
import type { Release, CreateReleaseData, UpdateReleaseData, ReleasesFilter } from '../../shared/types/entities';

export const useReleases = (filter: ReleasesFilter = {}) => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (type === 'success') {
      toast.success(message);
    } else if (type === 'error') {
      toast.error(message);
    } else {
      toast(message);
    }
  };

  const fetchReleases = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await releaseService.list(filter);
      setReleases(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch releases';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const createRelease = async (data: CreateReleaseData): Promise<Release> => {
    try {
      setError(null);
      const created = await releaseService.create(data);
      setReleases(prev => [created, ...prev]);
      showToast('Release created successfully', 'success');
      return created;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create release';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      throw err;
    }
  };

  const updateRelease = async (id: string, updates: UpdateReleaseData): Promise<Release> => {
    try {
      setError(null);
      const updated = await releaseService.update(id, updates);
      setReleases(prev => prev.map(r => r.id === id ? updated : r));
      showToast('Release updated successfully', 'success');
      return updated;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update release';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      throw err;
    }
  };

  const archiveRelease = async (id: string): Promise<void> => {
    try {
      setError(null);
      showToast('Archiving release...', 'info');
      await releaseService.archiveRelease(id);
      setReleases(prev => prev.filter(r => r.id !== id));
      showToast('Release archived successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to archive release';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const restoreRelease = async (id: string): Promise<void> => {
    try {
      setError(null);
      showToast('Restoring release...', 'info');
      await releaseService.restoreRelease(id);
      
      // Fetch the restored release and add it back to the list
      const { data: restoredRelease, error: fetchError } = await supabase
        .from('releases')
        .select(`
          *,
          artist:artists(*)
        `)
        .eq('id', id)
        .single();
      
      if (fetchError) {
        console.error('Failed to fetch restored release:', fetchError);
        // Fallback: just refetch all releases
        await refetch();
      } else if (restoredRelease) {
        // Add the restored release back to the list
        setReleases(prev => [restoredRelease, ...prev]);
      }
      
      showToast('Release restored successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore release';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const deleteRelease = async (id: string): Promise<void> => {
    try {
      setError(null);
      showToast('Deleting release...', 'info');
      await releaseService.deleteRelease(id);
      setReleases(prev => prev.filter(r => r.id !== id));
      showToast('Release deleted successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete release';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const getArchivedReleases = async (): Promise<Release[]> => {
    try {
      setError(null);
      const data = await releaseService.getArchivedReleases();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch archived releases';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      return [];
    }
  };

  useEffect(() => {
    fetchReleases();
  }, [JSON.stringify(filter)]);

  return {
    releases,
    loading,
    error,
    createRelease,
    updateRelease,
    archiveRelease,
    restoreRelease,
    deleteRelease,
    getArchivedReleases,
    refetch: fetchReleases
  };
};
